import { useState, useEffect } from 'react';
import { supabase, DJProfile } from '../../../lib/supabase';
import ImageCropModal from './ImageCropModal';

interface CropTarget {
  file: File;
  field: 'logo_url' | 'profile_image' | 'hero_image';
  aspectRatio?: number;
  title: string;
}

export default function ProfileManager() {
  const [profile, setProfile] = useState<DJProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadError, setUploadError] = useState<string>('');
  const [uploadSuccess, setUploadSuccess] = useState<string>('');
  const [cropTarget, setCropTarget] = useState<CropTarget | null>(null);

  const [form, setForm] = useState({
    name: '',
    bio: '',
    profile_image: '',
    logo_url: '',
    hero_image: '',
    hero_subtitle: '',
    hero_btn_primary: '',
    hero_btn_secondary: '',
    email: '',
    phone: '',
    location: '',
    instagram: '',
    facebook: '',
    twitter: '',
    youtube: '',
    spotify: '',
    soundcloud: '',
    tiktok: '',
    instagram_visible: true,
    facebook_visible: true,
    twitter_visible: true,
    youtube_visible: true,
    spotify_visible: true,
    soundcloud_visible: true,
    tiktok_visible: true,
    primary_color: '#14B8A6',
    secondary_color: '#0D9488',
    accent_color: '#F59E0B',
    text_color: '#1F2937',
    background_color: '#FFFFFF',
    // Section texts
    section_past_events_title: '',
    section_past_events_subtitle: '',
    section_past_events_description: '',
    section_gallery_title: '',
    section_gallery_subtitle: '',
    section_gallery_description: '',
    section_music_title: '',
    section_music_description: '',
    section_upcoming_title: '',
    section_upcoming_subtitle: '',
    section_upcoming_description: '',
    section_social_title: '',
    section_social_description: '',
    section_contact_title: '',
    section_contact_description: '',
    section_footer_bio: '',
    recovery_code: '',
    contact_form_url: '',
    contact_form_email: '',
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    const { data } = await supabase.from('dj_profile').select('*').maybeSingle();
    if (data) {
      setProfile(data);
      setForm({
        name: data.name || '',
        bio: data.bio || '',
        profile_image: data.profile_image || '',
        logo_url: (data as any).logo_url || '',
        hero_image: (data as any).hero_image || '',
        hero_subtitle: (data as any).hero_subtitle || '',
        hero_btn_primary: (data as any).hero_btn_primary || '',
        hero_btn_secondary: (data as any).hero_btn_secondary || '',
        email: data.email || '',
        phone: data.phone || '',
        location: data.location || '',
        instagram: data.instagram || '',
        facebook: data.facebook || '',
        twitter: data.twitter || '',
        youtube: data.youtube || '',
        spotify: data.spotify || '',
        soundcloud: data.soundcloud || '',
        tiktok: data.tiktok || '',
        instagram_visible: data.instagram_visible !== false,
        facebook_visible: data.facebook_visible !== false,
        twitter_visible: data.twitter_visible !== false,
        youtube_visible: data.youtube_visible !== false,
        spotify_visible: data.spotify_visible !== false,
        soundcloud_visible: data.soundcloud_visible !== false,
        tiktok_visible: data.tiktok_visible !== false,
        primary_color: (data as any).primary_color || '#14B8A6',
        secondary_color: (data as any).secondary_color || '#0D9488',
        accent_color: (data as any).accent_color || '#F59E0B',
        text_color: (data as any).text_color || '#1F2937',
        background_color: (data as any).background_color || '#FFFFFF',
        section_past_events_title: (data as any).section_past_events_title || '',
        section_past_events_subtitle: (data as any).section_past_events_subtitle || '',
        section_past_events_description: (data as any).section_past_events_description || '',
        section_gallery_title: (data as any).section_gallery_title || '',
        section_gallery_subtitle: (data as any).section_gallery_subtitle || '',
        section_gallery_description: (data as any).section_gallery_description || '',
        section_music_title: (data as any).section_music_title || '',
        section_music_description: (data as any).section_music_description || '',
        section_upcoming_title: (data as any).section_upcoming_title || '',
        section_upcoming_subtitle: (data as any).section_upcoming_subtitle || '',
        section_upcoming_description: (data as any).section_upcoming_description || '',
        section_social_title: (data as any).section_social_title || '',
        section_social_description: (data as any).section_social_description || '',
        section_contact_title: (data as any).section_contact_title || '',
        section_contact_description: (data as any).section_contact_description || '',
        section_footer_bio: (data as any).section_footer_bio || '',
        recovery_code: (data as any).recovery_code || 'recovery-2025',
        contact_form_url: (data as any).contact_form_url || 'https://readdy.ai/api/form/d6td8fi4of3kuoicn3l0',
        contact_form_email: (data as any).contact_form_email || data.email || '',
      });
    }
    setLoading(false);
  };

  const handleUpload = async (
    file: File,
    field: 'logo_url' | 'profile_image' | 'hero_image',
    setUploading: (v: boolean) => void,
    isCropped = false
  ) => {
    setUploading(true);
    setUploadError('');
    setUploadSuccess('');

    try {
      const ext = isCropped ? 'jpg' : (file.name.split('.').pop() || 'jpg');
      const fileName =
        field === 'logo_url'
          ? `logo_white_trans.${ext}`
          : field === 'hero_image'
          ? `hero_bg_${Date.now()}.${ext}`
          : `profile_${Date.now()}.${ext}`;

      const { data, error } = await supabase.storage
        .from('Fotos')
        .upload(fileName, file, { upsert: true });

      if (error) {
        console.error('Error al subir imagen:', error);
        setUploadError(`Error al subir la imagen: ${error.message}. Por favor, pega la URL directamente en el campo de texto.`);
        setUploading(false);
        return;
      }

      if (data) {
        const { data: urlData } = supabase.storage.from('Fotos').getPublicUrl(data.path);
        const newUrl = urlData.publicUrl;
        
        // Actualizar el formulario primero
        setForm((prev) => ({ ...prev, [field]: newUrl }));
        
        // Guardar automáticamente en la base de datos
        const updateData: Record<string, string> = {};
        (updateData as any)[field] = newUrl;
        
        if (profile?.id) {
          const { error: updateError } = await supabase
            .from('dj_profile')
            .update(updateData)
            .eq('id', profile.id);
          
          if (updateError) {
            console.error('Error al guardar:', updateError);
            setUploadError('Imagen subida pero no se pudo guardar en la base de datos');
          } else {
            setUploadSuccess('¡Imagen subida y guardada correctamente!');
            // Actualizar el profile state sin recargar todo el formulario
            setProfile((prev) => prev ? { ...prev, [field]: newUrl } as DJProfile : null);
            setTimeout(() => setUploadSuccess(''), 3000);
          }
        } else {
          setUploadSuccess('¡Imagen subida! Recuerda guardar los cambios.');
          setTimeout(() => setUploadSuccess(''), 3000);
        }
      }
    } catch (err) {
      console.error('Error inesperado:', err);
      setUploadError('Error al subir la imagen. Por favor, pega la URL directamente en el campo de texto.');
    }
    
    setUploading(false);
  };

  const handleFileSelected = (
    file: File,
    field: 'logo_url' | 'profile_image' | 'hero_image'
  ) => {
    const configs: Record<string, { aspectRatio?: number; title: string }> = {
      logo_url: { title: 'Recortar logo' },
      profile_image: { aspectRatio: 3 / 4, title: 'Recortar foto de perfil' },
      hero_image: { aspectRatio: 16 / 9, title: 'Recortar imagen de fondo' },
    };
    setCropTarget({ file, field, ...configs[field] });
  };

  const handleCropConfirm = async (croppedBlob: Blob) => {
    if (!cropTarget) return;
    const { field } = cropTarget;
    setCropTarget(null);

    const setUploading =
      field === 'logo_url'
        ? setUploadingLogo
        : field === 'hero_image'
        ? setUploadingHero
        : setUploadingPhoto;

    const croppedFile = new File([croppedBlob], 'cropped.jpg', { type: 'image/jpeg' });
    await handleUpload(croppedFile, field, setUploading, true);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus('idle');

    const emailChanged =
      profile?.email !== form.email &&
      form.email.trim() !== '' &&
      profile?.id != null;

    const updateData: Record<string, any> = {
      name: form.name,
      bio: form.bio,
      profile_image: form.profile_image,
      email: form.email,
      phone: form.phone,
      location: form.location,
      instagram: form.instagram,
      facebook: form.facebook,
      twitter: form.twitter,
      youtube: form.youtube,
      spotify: form.spotify,
      soundcloud: form.soundcloud,
      tiktok: form.tiktok,
      instagram_visible: form.instagram_visible,
      facebook_visible: form.facebook_visible,
      twitter_visible: form.twitter_visible,
      youtube_visible: form.youtube_visible,
      spotify_visible: form.spotify_visible,
      soundcloud_visible: form.soundcloud_visible,
      tiktok_visible: form.tiktok_visible,
    };

    if (form.logo_url) updateData.logo_url = form.logo_url;
    if (form.hero_image !== undefined) updateData.hero_image = form.hero_image;
    updateData.hero_btn_primary = form.hero_btn_primary;
    updateData.hero_btn_secondary = form.hero_btn_secondary;
    updateData.hero_subtitle = form.hero_subtitle;
    updateData.primary_color = form.primary_color;
    updateData.secondary_color = form.secondary_color;
    updateData.accent_color = form.accent_color;
    updateData.text_color = form.text_color;
    updateData.background_color = form.background_color;
    updateData.section_past_events_title = form.section_past_events_title;
    updateData.section_past_events_subtitle = form.section_past_events_subtitle;
    updateData.section_past_events_description = form.section_past_events_description;
    updateData.section_gallery_title = form.section_gallery_title;
    updateData.section_gallery_subtitle = form.section_gallery_subtitle;
    updateData.section_gallery_description = form.section_gallery_description;
    updateData.section_music_title = form.section_music_title;
    updateData.section_music_description = form.section_music_description;
    updateData.section_upcoming_title = form.section_upcoming_title;
    updateData.section_upcoming_subtitle = form.section_upcoming_subtitle;
    updateData.section_upcoming_description = form.section_upcoming_description;
    updateData.section_social_title = form.section_social_title;
    updateData.section_social_description = form.section_social_description;
    updateData.section_contact_title = form.section_contact_title;
    updateData.section_contact_description = form.section_contact_description;
    updateData.section_footer_bio = form.section_footer_bio;
    updateData.recovery_code = form.recovery_code;

    let error;
    if (profile?.id) {
      ({ error } = await supabase.from('dj_profile').update(updateData).eq('id', profile.id));
    } else {
      ({ error } = await supabase.from('dj_profile').insert([updateData]));
    }

    if (!error && emailChanged && profile?.id) {
      // Try to auto-generate a new form URL for the new email
      try {
        const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
        const res = await fetch(`${supabaseUrl}/functions/v1/update-contact-form-url`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: form.email, profileId: profile.id }),
        });
        const result = await res.json();
        if (result.success && result.formUrl) {
          setForm((prev) => ({
            ...prev,
            contact_form_url: result.formUrl,
            contact_form_email: form.email,
          }));
          setUploadSuccess('¡Email actualizado! El formulario de contacto enviará los mensajes al nuevo correo.');
          setTimeout(() => setUploadSuccess(''), 5000);
        } else {
          setUploadSuccess('Email guardado. El formulario seguirá enviando al correo anterior hasta que se actualice manualmente.');
          setTimeout(() => setUploadSuccess(''), 6000);
        }
      } catch {
        // Silent fail — not critical
      }
    }

    setSaveStatus(error ? 'error' : 'success');
    setSaving(false);
    if (!error) setTimeout(() => setSaveStatus('idle'), 3000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const sectionFields: Array<{
    key: string;
    label: string;
    icon: string;
    fields: Array<{ field: string; label: string; placeholder: string; multiline?: boolean }>;
  }> = [
    {
      key: 'past_events',
      label: 'Eventos Pasados',
      icon: 'ri-history-line',
      fields: [
        { field: 'section_past_events_subtitle', label: 'Etiqueta superior', placeholder: 'Eventos Pasados' },
        { field: 'section_past_events_title', label: 'Título principal', placeholder: 'Experiencias\nInolvidables' },
        { field: 'section_past_events_description', label: 'Descripción', placeholder: 'He tenido el honor de participar en eventos increíbles...', multiline: true },
      ],
    },
    {
      key: 'gallery',
      label: 'Galería',
      icon: 'ri-gallery-line',
      fields: [
        { field: 'section_gallery_subtitle', label: 'Etiqueta superior', placeholder: 'Galería' },
        { field: 'section_gallery_title', label: 'Título principal', placeholder: 'Momentos Únicos' },
        { field: 'section_gallery_description', label: 'Descripción', placeholder: 'Fotos y vídeos de las sesiones y eventos más especiales.', multiline: true },
      ],
    },
    {
      key: 'music',
      label: 'Música',
      icon: 'ri-music-2-line',
      fields: [
        { field: 'section_music_title', label: 'Título principal', placeholder: 'Mis Mixes' },
        { field: 'section_music_description', label: 'Descripción', placeholder: 'Escucha mis últimas sesiones y producciones', multiline: true },
      ],
    },
    {
      key: 'upcoming',
      label: 'Próximos Eventos',
      icon: 'ri-calendar-event-line',
      fields: [
        { field: 'section_upcoming_subtitle', label: 'Etiqueta superior', placeholder: 'Próximamente' },
        { field: 'section_upcoming_title', label: 'Título principal', placeholder: 'Eventos Confirmados' },
        { field: 'section_upcoming_description', label: 'Descripción', placeholder: 'No te pierdas mis próximas presentaciones...', multiline: true },
      ],
    },
    {
      key: 'social',
      label: 'Redes Sociales',
      icon: 'ri-share-line',
      fields: [
        { field: 'section_social_title', label: 'Título principal', placeholder: 'Sígueme en Redes' },
        { field: 'section_social_description', label: 'Descripción', placeholder: 'Mantente al día con mis últimas sesiones y lanzamientos', multiline: true },
      ],
    },
    {
      key: 'contact',
      label: 'Contacto',
      icon: 'ri-mail-send-line',
      fields: [
        { field: 'section_contact_title', label: 'Título principal', placeholder: 'Conectemos' },
        { field: 'section_contact_description', label: 'Descripción', placeholder: '¿Tienes un evento próximo? Hablemos...', multiline: true },
      ],
    },
  ];

  return (
    <div className="space-y-8">
      {/* Modal de recorte */}
      {cropTarget && (
        <ImageCropModal
          imageFile={cropTarget.file}
          aspectRatio={cropTarget.aspectRatio}
          title={cropTarget.title}
          onConfirm={handleCropConfirm}
          onCancel={() => setCropTarget(null)}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Perfil del DJ</h2>
          <p className="text-gray-500 text-sm mt-1">Gestiona tu información, logo y foto de perfil</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-white font-bold text-sm rounded-lg hover:shadow-[0_0_20px_rgba(6,182,212,0.4)] transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Guardando...
            </>
          ) : (
            <>
              <i className="ri-save-line"></i>
              Guardar cambios
            </>
          )}
        </button>
      </div>

      {saveStatus === 'success' && (
        <div className="flex items-center gap-2 bg-green-500/20 border border-green-500/50 rounded-lg px-4 py-3 text-green-400 text-sm">
          <i className="ri-checkbox-circle-line"></i>
          ¡Perfil guardado correctamente!
        </div>
      )}
      {saveStatus === 'error' && (
        <div className="flex items-center gap-2 bg-red-500/20 border border-red-500/50 rounded-lg px-4 py-3 text-red-400 text-sm">
          <i className="ri-error-warning-line"></i>
          Error al guardar. Inténtalo de nuevo.
        </div>
      )}
      {uploadSuccess && (
        <div className="flex items-center gap-2 bg-green-500/20 border border-green-500/50 rounded-lg px-4 py-3 text-green-400 text-sm">
          <i className="ri-checkbox-circle-line"></i>
          {uploadSuccess}
          <button onClick={() => setUploadSuccess('')} className="ml-auto flex-shrink-0 w-5 h-5 flex items-center justify-center hover:bg-green-500/20 rounded cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}
      {uploadError && (
        <div className="flex items-start gap-2 bg-yellow-500/20 border border-yellow-500/50 rounded-lg px-4 py-3 text-yellow-400 text-sm">
          <i className="ri-alert-line flex-shrink-0 mt-0.5"></i>
          <div>
            <p className="font-semibold mb-1">Problema al subir archivo</p>
            <p className="text-xs">{uploadError}</p>
          </div>
          <button onClick={() => setUploadError('')} className="ml-auto flex-shrink-0 w-5 h-5 flex items-center justify-center hover:bg-yellow-500/20 rounded cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        {/* LEFT COLUMN */}
        <div className="space-y-6">

          {/* Colores del tema */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <i className="ri-palette-line text-cyan-400"></i>
              Colores del tema
            </h3>
            <p className="text-gray-500 text-xs">Personaliza los colores principales de tu web</p>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Color primario', field: 'primary_color', placeholder: '#14B8A6' },
                { label: 'Color secundario', field: 'secondary_color', placeholder: '#0D9488' },
                { label: 'Color de acento', field: 'accent_color', placeholder: '#F59E0B' },
                { label: 'Color de texto', field: 'text_color', placeholder: '#1F2937' },
              ].map(({ label, field, placeholder }) => (
                <div key={field}>
                  <label className="block text-gray-400 text-xs uppercase tracking-wider mb-2">{label}</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={(form as any)[field]}
                      onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                      className="w-12 h-10 rounded cursor-pointer bg-black border border-gray-700"
                    />
                    <input
                      type="text"
                      value={(form as any)[field]}
                      onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                      className="flex-1 px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-xs placeholder-gray-600 focus:outline-none focus:border-cyan-400 transition-all"
                      placeholder={placeholder}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-black/50 border border-gray-700 rounded-lg p-4 mt-4">
              <p className="text-gray-400 text-xs mb-3">Vista previa:</p>
              <div className="flex gap-3">
                {['primary_color', 'secondary_color', 'accent_color', 'text_color'].map(f => (
                  <div key={f} className="w-12 h-12 rounded-lg border border-gray-700" style={{ backgroundColor: (form as any)[f] }}></div>
                ))}
              </div>
            </div>
          </div>

          {/* Logo */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <i className="ri-image-line text-cyan-400"></i>
              Logo del sitio
            </h3>
            <div className="flex items-center gap-6">
              <div className="w-32 h-20 bg-black rounded-lg border border-gray-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                {form.logo_url ? (
                  <img src={form.logo_url} alt="Logo" className="w-full h-full object-contain p-2" />
                ) : (
                  <i className="ri-image-line text-gray-600 text-2xl"></i>
                )}
              </div>
              <div className="flex-1 space-y-3">
                <label className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/50 text-cyan-400 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-crop-line"></i>
                  {uploadingLogo ? 'Subiendo...' : 'Seleccionar y recortar'}
                  <input
                    type="file" accept="image/*"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelected(f, 'logo_url'); e.target.value = ''; }}
                    className="hidden" disabled={uploadingLogo}
                  />
                </label>
                <input
                  type="text" value={form.logo_url}
                  onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                  placeholder="O pega aquí la URL del logo"
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-xs placeholder-gray-600 focus:outline-none focus:border-cyan-400 transition-all"
                />
                <p className="text-gray-500 text-xs">
                  <i className="ri-information-line"></i> También puedes subir a <a href="https://imgur.com/upload" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">Imgur</a> y pegar la URL
                </p>
              </div>
            </div>
          </div>

          {/* Foto de fondo del Hero */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <i className="ri-landscape-line text-cyan-400"></i>
              Imagen de fondo del Hero
            </h3>
            <div className="space-y-4">
              <div className="w-full h-40 bg-black rounded-lg border border-gray-700 overflow-hidden relative">
                {form.hero_image ? (
                  <img src={form.hero_image} alt="Hero background" className="w-full h-full object-cover object-top" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                    <i className="ri-image-2-line text-gray-600 text-3xl"></i>
                    <span className="text-gray-600 text-xs">Sin imagen de fondo</span>
                  </div>
                )}
                {form.hero_image && (
                  <button
                    onClick={() => setForm((prev) => ({ ...prev, hero_image: '' }))}
                    className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-black/70 hover:bg-red-500/80 rounded-full text-white text-xs transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                )}
              </div>
              <label className="flex items-center justify-center gap-2 px-4 py-2.5 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/50 text-cyan-400 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-crop-line"></i>
                {uploadingHero ? 'Subiendo...' : 'Seleccionar y recortar'}
                <input
                  type="file" accept="image/*"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelected(f, 'hero_image'); e.target.value = ''; }}
                  className="hidden" disabled={uploadingHero}
                />
              </label>
              <input
                type="text" value={form.hero_image}
                onChange={(e) => setForm({ ...form, hero_image: e.target.value })}
                placeholder="O pega aquí la URL de la imagen de fondo"
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-xs placeholder-gray-600 focus:outline-none focus:border-cyan-400 transition-all"
              />
            </div>
          </div>

          {/* Foto de perfil */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <i className="ri-user-line text-cyan-400"></i>
              Foto de perfil
            </h3>
            <div className="flex items-center gap-6">
              <div className="w-24 h-32 bg-black rounded-lg border border-gray-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                {form.profile_image ? (
                  <img src={form.profile_image} alt="Perfil" className="w-full h-full object-cover object-top" />
                ) : (
                  <i className="ri-user-line text-gray-600 text-2xl"></i>
                )}
              </div>
              <div className="flex-1 space-y-3">
                <label className="flex items-center justify-center gap-2 px-4 py-2.5 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/50 text-cyan-400 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-crop-line"></i>
                  {uploadingPhoto ? 'Subiendo...' : 'Seleccionar y recortar'}
                  <input
                    type="file" accept="image/*"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelected(f, 'profile_image'); e.target.value = ''; }}
                    className="hidden" disabled={uploadingPhoto}
                  />
                </label>
                <input
                  type="text" value={form.profile_image}
                  onChange={(e) => setForm({ ...form, profile_image: e.target.value })}
                  placeholder="O pega aquí la URL de la foto"
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-xs placeholder-gray-600 focus:outline-none focus:border-cyan-400 transition-all"
                />
              </div>
            </div>
          </div>

          {/* Nombre y Bio */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <i className="ri-file-text-line text-cyan-400"></i>
              Información general
            </h3>
            <div>
              <label className="block text-gray-400 text-xs uppercase tracking-wider mb-2">Nombre artístico</label>
              <input
                type="text" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400 transition-all"
                placeholder="Nombre del DJ"
              />
            </div>
            <div>
              <label className="block text-gray-400 text-xs uppercase tracking-wider mb-2">Biografía</label>
              <textarea
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                rows={5}
                className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400 transition-all resize-none"
                placeholder="Escribe tu biografía..."
              />
            </div>
          </div>

          {/* Botones del Hero */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <i className="ri-cursor-line text-cyan-400"></i>
              Textos del Hero
            </h3>
            <p className="text-gray-500 text-xs">Personaliza el subtítulo y los botones de la sección principal</p>
            <div>
              <label className="block text-gray-400 text-xs uppercase tracking-wider mb-2">Subtítulo</label>
              <input
                type="text" value={form.hero_subtitle}
                onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })}
                className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400 transition-all"
                placeholder="DJ Profesional"
              />
            </div>
            <div>
              <label className="block text-gray-400 text-xs uppercase tracking-wider mb-2">Botón principal</label>
              <input
                type="text" value={form.hero_btn_primary}
                onChange={(e) => setForm({ ...form, hero_btn_primary: e.target.value })}
                className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400 transition-all"
                placeholder="Reservar Ahora"
              />
            </div>
            <div>
              <label className="block text-gray-400 text-xs uppercase tracking-wider mb-2">Botón secundario</label>
              <input
                type="text" value={form.hero_btn_secondary}
                onChange={(e) => setForm({ ...form, hero_btn_secondary: e.target.value })}
                className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400 transition-all"
                placeholder="Ver Eventos"
              />
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-6">

          {/* Textos de las secciones */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-6">
            <div>
              <h3 className="text-white font-semibold flex items-center gap-2">
                <i className="ri-layout-line text-cyan-400"></i>
                Textos de las secciones
              </h3>
              <p className="text-gray-500 text-xs mt-1">Personaliza los títulos y descripciones de cada sección de la web. Si lo dejas vacío se usará el texto por defecto.</p>
            </div>

            {sectionFields.map((section) => (
              <div key={section.key} className="border border-gray-800 rounded-lg overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 bg-black/40 border-b border-gray-800">
                  <i className={`${section.icon} text-fuchsia-400 text-sm`}></i>
                  <span className="text-white text-sm font-semibold">{section.label}</span>
                </div>
                <div className="p-4 space-y-3">
                  {section.fields.map(({ field, label, placeholder, multiline }) => (
                    <div key={field}>
                      <label className="block text-gray-400 text-xs uppercase tracking-wider mb-1.5">{label}</label>
                      {multiline ? (
                        <textarea
                          value={(form as any)[field]}
                          onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                          rows={2}
                          className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400 transition-all resize-none"
                          placeholder={placeholder}
                        />
                      ) : (
                        <input
                          type="text"
                          value={(form as any)[field]}
                          onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                          className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400 transition-all"
                          placeholder={placeholder}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Contacto */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <i className="ri-contacts-line text-cyan-400"></i>
              Datos de contacto
            </h3>
            {[
              { label: 'Email', field: 'email', icon: 'ri-mail-line', placeholder: 'tu@email.com', type: 'email' },
              { label: 'Teléfono', field: 'phone', icon: 'ri-phone-line', placeholder: '+34 600 000 000', type: 'text' },
              { label: 'Ubicación', field: 'location', icon: 'ri-map-pin-line', placeholder: 'Ciudad, País', type: 'text' },
            ].map(({ label, field, icon, placeholder, type }) => (
              <div key={field}>
                <label className="block text-gray-400 text-xs uppercase tracking-wider mb-2">{label}</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className={`${icon} text-gray-500 text-sm`}></i>
                  </div>
                  <input
                    type={type}
                    value={(form as any)[field]}
                    onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-black border border-gray-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400 transition-all"
                    placeholder={placeholder}
                  />
                </div>
              </div>
            ))}
            {/* Info sobre el formulario */}
            <div className="mt-2 flex items-start gap-2 bg-cyan-500/10 border border-cyan-500/20 rounded-lg px-4 py-3">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-information-line text-cyan-400 text-sm"></i>
              </div>
              <div className="text-xs text-cyan-300 space-y-0.5">
                <p className="font-semibold">Formulario de contacto</p>
                <p>Los mensajes del formulario se envían a: <span className="font-mono font-bold">{form.contact_form_email || form.email || '—'}</span></p>
                {form.email && form.contact_form_email && form.email !== form.contact_form_email && (
                  <p className="text-yellow-400 font-semibold mt-1">
                    <i className="ri-alert-line mr-1"></i>
                    El email ha cambiado. Guarda los cambios para actualizar el formulario.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Redes sociales */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
            <div>
              <h3 className="text-white font-semibold flex items-center gap-2">
                <i className="ri-share-line text-cyan-400"></i>
                Redes sociales
              </h3>
              <p className="text-gray-500 text-xs mt-1">Usa el ojo para mostrar u ocultar cada red en tu web.</p>
            </div>
            {[
              { label: 'Instagram', field: 'instagram', visField: 'instagram_visible', icon: 'ri-instagram-line', placeholder: 'https://instagram.com/...' },
              { label: 'Facebook', field: 'facebook', visField: 'facebook_visible', icon: 'ri-facebook-fill', placeholder: 'https://facebook.com/...' },
              { label: 'TikTok', field: 'tiktok', visField: 'tiktok_visible', icon: 'ri-tiktok-line', placeholder: 'https://tiktok.com/@...' },
              { label: 'YouTube', field: 'youtube', visField: 'youtube_visible', icon: 'ri-youtube-line', placeholder: 'https://youtube.com/...' },
              { label: 'Spotify', field: 'spotify', visField: 'spotify_visible', icon: 'ri-spotify-line', placeholder: 'https://open.spotify.com/...' },
              { label: 'SoundCloud', field: 'soundcloud', visField: 'soundcloud_visible', icon: 'ri-soundcloud-line', placeholder: 'https://soundcloud.com/...' },
              { label: 'Twitter / X', field: 'twitter', visField: 'twitter_visible', icon: 'ri-twitter-x-line', placeholder: 'https://x.com/...' },
            ].map(({ label, field, visField, icon, placeholder }) => {
              const isVisible = (form as any)[visField] as boolean;
              return (
                <div key={field}>
                  <label className="block text-gray-400 text-xs uppercase tracking-wider mb-2">{label}</label>
                  <div className="flex items-center gap-2">
                    {/* Toggle visibility */}
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, [visField]: !isVisible })}
                      title={isVisible ? 'Ocultar en la web' : 'Mostrar en la web'}
                      className={`flex-shrink-0 w-9 h-10 flex items-center justify-center rounded-lg border transition-all cursor-pointer ${
                        isVisible
                          ? 'bg-cyan-500/20 border-cyan-400/50 text-cyan-400 hover:bg-cyan-500/30'
                          : 'bg-gray-800 border-gray-700 text-gray-600 hover:border-gray-500'
                      }`}
                    >
                      <i className={`text-base ${isVisible ? 'ri-eye-line' : 'ri-eye-off-line'}`}></i>
                    </button>
                    {/* Input */}
                    <div className={`relative flex-1 transition-opacity ${isVisible ? 'opacity-100' : 'opacity-40'}`}>
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                        <i className={`${icon} text-gray-500 text-sm`}></i>
                      </div>
                      <input
                        type="text"
                        value={(form as any)[field]}
                        onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                        className="w-full pl-10 pr-4 py-3 bg-black border border-gray-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400 transition-all"
                        placeholder={placeholder}
                      />
                    </div>
                    {/* Badge estado */}
                    <span className={`flex-shrink-0 text-xs px-2 py-1 rounded-md font-semibold whitespace-nowrap ${
                      isVisible
                        ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                        : 'bg-gray-800 text-gray-500 border border-gray-700'
                    }`}>
                      {isVisible ? 'Visible' : 'Oculta'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Código de recuperación */}
          <div className="bg-gray-900 border border-fuchsia-500/20 rounded-xl p-6 space-y-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <i className="ri-key-2-line text-fuchsia-400"></i>
              Código de recuperación
            </h3>
            <p className="text-gray-500 text-xs">
              Este código te permite restablecer tu contraseña si alguna vez la olvidas. Guárdalo en un lugar seguro.
            </p>
            <div className="flex items-start gap-3 bg-fuchsia-500/10 border border-fuchsia-500/20 rounded-lg px-4 py-3">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-shield-keyhole-line text-fuchsia-400 text-sm"></i>
              </div>
              <p className="text-fuchsia-300 text-xs">
                Para usarlo, ve a la pantalla de login → "Cambiar contraseña" → pestaña "Olvidé mi contraseña" e introduce este código.
              </p>
            </div>
            <div>
              <label className="block text-gray-400 text-xs uppercase tracking-wider mb-2">Tu código de recuperación</label>
              <input
                type="text"
                value={form.recovery_code}
                onChange={(e) => setForm({ ...form, recovery_code: e.target.value })}
                className="w-full px-4 py-3 bg-black border border-fuchsia-700/50 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-fuchsia-400 transition-all font-mono"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Save bottom */}
      <div className="flex justify-end pt-4 border-t border-gray-800">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-white font-bold text-sm rounded-lg hover:shadow-[0_0_20px_rgba(6,182,212,0.4)] transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Guardando...
            </>
          ) : (
            <>
              <i className="ri-save-line"></i>
              Guardar todos los cambios
            </>
          )}
        </button>
      </div>
    </div>
  );
}
